import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Container,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { FormattedMessage } from "react-intl";

import {
  playerPremiumData,
  teamPremiumData,
} from "~/ui/components/premiumModal/premiumData";
import { useScreenMd, useScreenSm } from "~/utils";

import { FooterIcons, FooterList, FooterStores } from "./footer/index";

export const Footer: React.FC = () => {
  const isDesktop = useScreenMd();
  const isMobile = useScreenSm();
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const [teamPremiumOpen, setTeamPremiumOpen] = React.useState(false);

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        bgcolor: "background.paper",
        pt: 2,
        pb: 1,
        borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, ${
            theme.palette.primary.main
          } 0%, ${alpha(theme.palette.primary.main, 0.3)} 100%)`,
          zIndex: 2,
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 0 } }}>
        <Box
          display="flex"
          flexDirection={isDesktop ? "row" : "column"}
          justifyContent="space-between"
          alignItems={isDesktop ? "flex-start" : "stretch"}
          gap={2}
        >
          <Box
            flex={1}
            minWidth={220}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
              maxWidth: { xs: "100%", md: 320 },
              width: "100%",
            }}
          >
            <FooterIcons />
            {!isDesktop && <FooterList />}
            <FooterStores sx={{ mt: 2 }} />
          </Box>
          <Box
            flex={1}
            minWidth={220}
            sx={{
              px: isDesktop ? 2 : 0,
              py: 2,
              mx: isDesktop ? 1 : 0,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              borderRadius: 3,
              boxShadow: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
              transition: "box-shadow 0.2s",
              "&:hover": {
                boxShadow: 6,
              },
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="primary"
              gutterBottom
              sx={{
                letterSpacing: "0.02em",
                mb: 1,
                pl: { xs: 1, sm: 0 },
              }}
            >
              <FormattedMessage defaultMessage="Player Premium" id="0Hn96j" />
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 0,
                m: 0,
                listStyle: "none",
                color: theme.palette.text.secondary,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.body2.fontSize,
                lineHeight: 1.7,
              }}
            >
              {playerPremiumData.map((item, idx) => (
                <Box
                  key={idx}
                  component="li"
                  display="flex"
                  alignItems="flex-start"
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    transition: "background 0.2s",
                    "&:hover": {
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      bgcolor: theme.palette.primary.main,
                      borderRadius: "50%",
                      mt: "7px",
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  />
                  <Box component="span">{item}</Box>
                </Box>
              ))}
            </Box>
          </Box>
          {isMobile ? (
            <Accordion
              expanded={teamPremiumOpen}
              onChange={(_, expanded) => setTeamPremiumOpen(expanded)}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderRadius: 3,
                boxShadow: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
                mb: 0,
                "&:before": { display: "none" },
                transition: "box-shadow 0.2s",
                "&:hover": {
                  boxShadow: 6,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />
                }
                aria-controls="team-premium-content"
                id="team-premium-header"
                sx={{
                  px: 2,
                  minHeight: 48,
                  "& .MuiAccordionSummary-content": {
                    my: 0.5,
                  },
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="primary"
                    sx={{
                      letterSpacing: "0.02em",
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="Team Premium"
                      id="1f4t1I"
                    />
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, pl: 0.5 }}
                  >
                    At least two players in the squad must own Premium
                    membership.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0 }}>
                <Box
                  component="ul"
                  sx={{
                    pl: 0,
                    m: 0,
                    listStyle: "none",
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: 1.7,
                  }}
                >
                  {teamPremiumData.map((item, idx) => (
                    <Box
                      key={idx}
                      component="li"
                      display="flex"
                      alignItems="flex-start"
                      sx={{
                        mb: 0.5,
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        transition: "background 0.2s",
                        "&:hover": {
                          background: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          bgcolor: theme.palette.primary.main,
                          borderRadius: "50%",
                          mt: "7px",
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                      <Box component="span">{item}</Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box
              flex={1}
              minWidth={220}
              sx={{
                px: isDesktop ? 2 : 0,
                py: 2,
                mx: isDesktop ? 1 : 0,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderRadius: 3,
                boxShadow: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
                transition: "box-shadow 0.2s",
                "&:hover": {
                  boxShadow: 6,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="primary"
                gutterBottom
                sx={{
                  letterSpacing: "0.02em",
                  mb: 0.5,
                  pl: { xs: 1, sm: 0 },
                }}
              >
                <FormattedMessage defaultMessage="Team Premium" id="1f4t1I" />
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, pl: { xs: 1, sm: 0 } }}
              >
                At least two players in the squad must own Premium membership.
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 0,
                  m: 0,
                  listStyle: "none",
                  color: theme.palette.text.secondary,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: 1.7,
                }}
              >
                {teamPremiumData.map((item, idx) => (
                  <Box
                    key={idx}
                    component="li"
                    display="flex"
                    alignItems="flex-start"
                    sx={{
                      mb: 0.5,
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      transition: "background 0.2s",
                      "&:hover": {
                        background: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        bgcolor: theme.palette.primary.main,
                        borderRadius: "50%",
                        mt: "7px",
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />
                    <Box component="span">{item}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 3,
            mb: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            variant="text"
            color="primary"
            sx={{
              fontWeight: 600,
              minWidth: 100,
              textTransform: "none",
            }}
            href="/read-more"
          >
            <FormattedMessage defaultMessage="Read More" id="4Cltcc" />
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              boxShadow: 4,
              px: 4,
              py: 1.2,
              fontSize: "1rem",
              letterSpacing: "0.03em",
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
              color: theme.palette.primary.contrastText,
              transition: "transform 0.15s, box-shadow 0.15s",
              "&:hover": {
                background: `linear-gradient(90deg, ${theme.palette.primary.light} 60%, ${theme.palette.primary.main} 100%)`,
                boxShadow: 8,
                transform: "scale(1.04)",
              },
            }}
            href="/shop"
          >
            <FormattedMessage defaultMessage="Get Premium" id="0WPNdr" />
          </Button>

          <Button
            variant="text"
            color="primary"
            sx={{
              fontWeight: 600,
              minWidth: 100,
              textTransform: "none",
            }}
            href="/news/61f2a9e4c9ca0ff0983ef257"
          >
            <FormattedMessage defaultMessage="Rules" id="kAAlGL" />
          </Button>
        </Box>

        <Divider
          sx={{ my: 1.5, borderColor: alpha(theme.palette.primary.main, 0.2) }}
        />

        <Box
          display="flex"
          flexDirection={isDesktop ? "row" : "column"}
          justifyContent="space-between"
          alignItems={isDesktop ? "center" : "flex-start"}
          gap={1}
          sx={{ mt: 0, mb: 0 }}
        >
          <Typography variant="body2" fontWeight={700} color="text.primary">
            <FormattedMessage
              defaultMessage="© 2016 - {currentYear} Virtual Pro Gaming, Inc."
              id="BvTl/p"
              values={{ currentYear }}
            />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage
              defaultMessage="This website is not affiliated with or sponsored by Electronic Arts Inc. or its licensors."
              id="8vfVOh"
            />
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
